import {
  BlightChartDetailAPIResponse,
  BlightChartDetailsDayAPIResponse,
  BlightChartDetailsResultAPIResponse,
  BlightCropFieldDataWrapperAPIResponse,
  BlightDataAPIResponse,
  BlightResultAPIResponse,
  CustomerBlightRightsAPIResponse,
  DayAPIResponse,
  GetParticipatingCropFieldsResponse,
  ParticipatingCropFieldAPIResponse,
} from './api-models';
import {
  BlightChartDetail,
  BlightChartDetailsDay,
  BlightChartDetailsResult,
  BlightCropFieldAdvice,
  BlightData,
  BlightResult,
  CustomerBlightRights,
  DailyAdvice,
  Day,
  ParticipatingCropField,
} from './models';
import { getAdvice } from '../shared/helpers/spray.helpers';

export const mapCropFields = (blightData: BlightData, cropField: ParticipatingCropField):
  BlightCropFieldAdvice =>
({
  cropFieldId: blightData.cropFieldId,
  cropFieldName: cropField.name,
  cropVarietyName: blightData.cropVarietyName ?? cropField.cropVarietyName,
  amountOfHectares: cropField.areaInHectares,
  cropYear: blightData.year,
  emergenceDate: blightData.emergenceDate,
  plantDate: blightData.plantDate,
  harvestDate: blightData.harvestDate,
  advices: blightData.blightResult?.days?.map(mapAdvices) ?? [],
  todaysAdvice: blightData.blightResult?.today ? mapAdvices(blightData.blightResult?.today) : undefined,
  lastSprayDate: blightData.lastSprayDate,
  isCropFieldVisible: blightData.isCropFieldVisible,
});

const mapAdvices = (day: Day): DailyAdvice =>
({
  date: new Date(day.date),
  sprayAdvice: getAdvice(day.sprayType),
  remainingFungicidePercentage: day.fungicideLeftPct,
  isSprayedToday: !!day.sprayTime,
});

// region Blight Data

export const mapParticipatingCropFieldAPIResponses = (responses: GetParticipatingCropFieldsResponse): ParticipatingCropField[] =>
  responses.cropFields.map(mapParticipatingCropFieldAPIResponse);

export const mapBlightDataAPIResponses = (responses: BlightDataAPIResponse[]): BlightData[] =>
  responses.map(mapBlightDataAPIResponse);

export const mapBlightDataDayAPIResponses = (responses: DayAPIResponse[]): Day[] =>
  responses.map(mapBlightDataDayAPIResponse);

export const mapParticipatingCropFieldAPIResponse = (response: ParticipatingCropFieldAPIResponse): ParticipatingCropField => ({
  id: response.cropFieldId,
  name: response.cropFieldName,
  areaInHectares: response.areaInHectares,
  cropId: response.cropId,
});

export const mapBlightDataAPIResponse = (response: BlightDataAPIResponse): BlightData => ({
  ...response,
  blightResult: response.blightResult ? mapBlightResultAPIResponse(response.blightResult) : undefined,
  plantDate: response.plantDate ? new Date(response.plantDate) : undefined,
    plantDateCreatedAt: response.plantDateCreatedAt ? new Date(response.plantDateCreatedAt) : undefined,
    plantDateUpdatedAt: response.plantDateUpdatedAt ? new Date(response.plantDateUpdatedAt) : undefined,
  harvestDate: response.harvestDate ? new Date(response.harvestDate) : undefined,
  emergenceDate: response.emergenceDate ? new Date(response.emergenceDate) : undefined,
  lastSprayDate: response.lastSprayDate ? new Date(response.lastSprayDate) : undefined,
});

export const mapBlightResultAPIResponse = (response: BlightResultAPIResponse): BlightResult => ({
  ...response,
  calculatedOn: new Date(response?.calculatedOn),
  days: mapBlightDataDayAPIResponses(response.days),
  today: response.today ? mapBlightDataDayAPIResponse(response.today) : undefined,
});

export const mapBlightDataDayAPIResponse = (response: DayAPIResponse): Day => ({
  ...response,
  date: new Date(response.date),
  sprayTime: response.sprayTime ? new Date(response.sprayTime) : undefined,
});

// endregion

// region Customer Blight Rights

export const mapCustomerBlightRightsAPIResponse = (response: CustomerBlightRightsAPIResponse): CustomerBlightRights => ({
  ...response,
});

// endregion

// region Blight Chart Details
export const mapBlightChartDetailAPIResponses = (responses: BlightCropFieldDataWrapperAPIResponse): BlightChartDetail[] =>
  [mapBlightChartDetailAPIResponse(responses.cropField)];

export const mapBlightChartDetailsDayAPIResponses = (responses: BlightChartDetailsDayAPIResponse[]): BlightChartDetailsDay[] =>
  responses.map(mapBlightChartDetailsDayAPIResponse);

export const mapBlightChartDetailAPIResponse = (response: BlightChartDetailAPIResponse): BlightChartDetail => ({
  ...response,
  blightResult: mapBlightChartDetailsResultAPIResponse(response.blightResult),
});

export const mapBlightChartDetailsResultAPIResponse = (response: BlightChartDetailsResultAPIResponse | undefined): BlightChartDetailsResult => ({
  ...response,
  calculatedOn: response?.calculatedOn ? new Date(response.calculatedOn) : null,
  days: response?.days ? mapBlightChartDetailsDayAPIResponses(response.days) : [],
});

export const mapBlightChartDetailsDayAPIResponse = (response: BlightChartDetailsDayAPIResponse): BlightChartDetailsDay => ({
  ...response,
  date: new Date(response.date),
  sprayTime: response.sprayTime ? new Date(response.sprayTime) : null,
});

// endregion

